import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Plus, Check, Share2, Layers, ArrowLeft } from "lucide-react";
import api from "../api";
import SEO from "../components/SEO";
import ProductCard from "../components/ProductCard";
import { useCart } from "../contexts/CartContext";
import { loadProducts } from "../components/ResearchStacks";

const SITE = "https://prosourcelabs.org";

function ShareButton({ url, title }) {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch { /* fall through to copy */ }
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button data-testid="stack-page-share" onClick={share} className="btn-ghost" style={{ padding: "14px 20px" }}>
      {copied ? <Check size={14} /> : <Share2 size={14} />} {copied ? "Link copied" : "Share"}
    </button>
  );
}

export default function StackPage() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [stack, setStack] = useState(null);
  const [missing, setMissing] = useState(false);
  const [all, setAll] = useState([]);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setStack(null); setMissing(false);
    api.get(`/research-stacks/${slug}`).then(({ data }) => setStack(data)).catch(() => setMissing(true));
    loadProducts().then(setAll);
  }, [slug]);

  if (missing) return <div data-testid="stack-page-missing" className="max-w-3xl mx-auto px-6 py-32 text-center text-[#8A94A6]">This stack isn't available. <Link to="/stacks" className="text-[#00C3FF]">See all stacks</Link></div>;
  if (!stack) return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-[#5C657A] font-mono-tech text-xs">Loading…</div>;

  const bySlug = Object.fromEntries(all.map((p) => [p.slug, p]));
  const products = stack.slugs.map((s) => bySlug[s]).filter((p) => p?.variants?.length);
  const pct = Number(stack.percent_off || 0);
  const regular = products.reduce((s, p) => s + Number(p.variants[0].price), 0);
  const stackPrice = products.length === stack.slugs.length ? regular * (1 - pct / 100) : regular;
  const url = `${SITE}/stacks/${stack.slug}`;
  const addAll = () => { products.forEach((p) => addItem(p, p.variants[0], 1)); setAdded(true); setTimeout(() => setAdded(false), 2200); };

  return (
    <div data-testid="stack-page" className="max-w-7xl mx-auto px-6 py-16">
      <SEO
        title={`${stack.name} — ${products.map((p) => p.name).join(" + ")}`}
        description={stack.description || `${stack.name}: ${products.map((p) => p.name).join(", ")}. Save ${pct}% when researched together.`}
        path={`/stacks/${stack.slug}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "ItemList", name: stack.name, itemListElement: products.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE}/product/${p.slug}`, name: p.name })) }}
      />
      <Link to="/stacks" data-testid="stack-page-back" className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-[#8A94A6] hover:text-white"><ArrowLeft size={12} /> All Stacks</Link>
      <div className="mt-8 max-w-3xl">
        <div className="overline flex items-center gap-2"><Layers size={12} /> RESEARCH STACK</div>
        <h1 data-testid="stack-page-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light mt-3">{stack.name}</h1>
        {stack.description && <p data-testid="stack-page-description" className="text-base text-[#8A94A6] mt-6 leading-relaxed">{stack.description}</p>}
        <div className="flex flex-wrap items-end gap-6 mt-8">
          <div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#5C657A]">Stack price · 1 vial each</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span data-testid="stack-page-price" className="font-mono-tech text-3xl text-[#00C3FF]">${stackPrice.toFixed(2)}</span>
              {pct > 0 && <span className="font-mono-tech text-sm text-[#5C657A] line-through">${regular.toFixed(2)}</span>}
              {pct > 0 && <span data-testid="stack-page-save" className="px-2 py-1 border border-[#00C3FF]/50 text-[#00C3FF] font-mono-tech text-[10px] uppercase tracking-[0.18em]">Save {pct}%</span>}
            </div>
          </div>
          <button data-testid="stack-page-add" onClick={addAll} className="btn-primary" style={{ padding: "14px 24px" }}>
            {added ? <><Check size={14} /> Added</> : <><Plus size={14} /> Add Stack to Cart</>}
          </button>
          <ShareButton url={url} title={stack.name} />
        </div>
        <p className="font-mono-tech text-[10px] text-[#5C657A] mt-5 uppercase tracking-wider">Stack price applies automatically at checkout when every compound is in your cart (1–{stack.max_line_qty} vials each). For research use only. Not for human consumption.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mt-14">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
    </div>
  );
}
